import {Contact} from "../contact/contact";
/**
 * Model for the full input of a donation and some helber functions
 */
export class Donation {

    /**
     * Define the available transactions types
     * @type {string}
     */
    public static readonly TRANSACTION_TYPE_DONATION = 'donation';
    public static readonly TRANSACTION_TYPE_MEMBERSHIP = 'membership';
    public static readonly TRANSACTION_TYPE_MEMBERSHIP_SPONSORING = 'membership_sponsoring';
    public static readonly TRANSACTION_TYPE_EVENT = 'event';

    /**
     *
     * @type {string}
     */
    public transactionType = Donation.TRANSACTION_TYPE_DONATION;

    /**
     * Holds the transaction GUID
     * @type {null}
     */
    public transactionGuid: string = null;

    /**
     * Amount to donate. Set the default here
     * @type {number}
     */
    public amount = 0;

    /**
     * extra amount that should be added to the donation
     * @type {number}
     */
    public extraAmount = 0;

    /**
     * Currency of the donation
     *
     * @type {string}
     */
    public currency = "EUR";

    /**
     * Symbol for the currency
     *
     * @type {string}
     */
    public currencySymbol = "€";

    /**
     * Could be 'one_time' or 'recurring'
     */
    public rythm: string = null;

    /**
     * If donation rythm is recurring, the interval
     */
    public recurringRythm: string = null;

    /**
     * donation target
     */
    public target = "";

    /**
     * donation target internal id (if set)
     */
    public targetInternalId: string = null;

    /**
     * Holds the users e-mail adress
     */
    public userEmail: string = null;

    /**
     * Holds the users countrycode
     */
    public userCountry: string = null;

    /**
     * Holds the users currently used language
     */
    public userLanguage: string = null;

    /**
     * payment method
     */
    public paymentMethod: string = null;

    /**
     * Holds data for debit payment
     */
    public paymentDebit: PaymentDebit = new PaymentDebit();

    /**
     * Holds data for banktransfer
     */
    public paymentBanktransfer: PaymentBanktransfer = new PaymentBanktransfer();

    /**
     * Holds some Metadate for the donation
     * @type {MetaData}
     */
    public metaData: MetaData = new MetaData();

    /**
     *
     * @type {String}
     */
    public paymentUrl = "";

    /**
     * HOlds the url on which the transaction was started.
     */
    public url = "";

    /**
     * holds optional some contact data
     * @type {Contact}
     */
    public contactData: Contact = null;

    /**
     * The trigger of the donation (optional)
     *
     * @type {String}
     */
    public trigger: string = null;

    /**
     * The eventId of the donation if there is one (optional)
     *
     * @type {Number}
     */
    public eventId: number = null;

  /**
   * The internal eventId of the donation if there is one (optional)
   *
   * @type {String}
   */
    public eventInternalId: string = null;

    /**
     * Holds the special eventdata of the transaction
     *
     * @type {DonatorData}
     */
    public donatorData: DonatorData;

    /**
     * Holds the special data for a gift donation
     */
    public giftData: GiftData = new GiftData();

    /**
     * HOlds the data that is neede for a gift doczument of (shop) donations.
     */
    public gift: Gift;

    /**
     * remarks from the user for this donation.
     */
    public remarks: string = null;

    /**
     * Holds the information for a bonus that was selected by the user.
     */
    public bonus: string = null;

    /**
     * products that are part of this donation if this is done from a shop.
     */
    public products: {
        id: number,
        product: string,
        productPlural: string,
        productInternalId: string,
        count: number,
        price: number,
        categories: string[],
        giftSender: string,
        giftReceiver: string,
        giftMessage: string,
        productText: string,
        text: string
    }[] = [];

    /**
     * Some additional data that can be set by custom widgets
     */
    public additional: any = {};

    /**
     * Copies dta from an anonymous object into the current donation object.
     * @param data
     */
    public copyData(data) {
      for (const key in data) {
        if (this.hasOwnProperty(key)) {
          if (typeof this[key] === "object" && this[key] !== null) {
            if (data[key]) {
              this[key] = Object.assign(this[key], data[key]);
            }
          }
          else {
            this[key] = data[key];
          }
        }
      }
    }

    /**
     * Checks if the amount is zero.
     */
    public isAmountZero(): boolean {
        return this.amount + this.extraAmount === 0;
    }

    /**
     * Generate a validation hash, based on the token and the current timestamp
     */
    public async generateHash() {
      const ts = Date.now().toString();
      const msg = `${ts}:${this.metaData.token}`;
      let th = 'no-cryptop';

      if (this.supportsWebCryptoDigest()) {
        const encoder = new TextEncoder();
        const data = encoder.encode(msg);
        const buf = await crypto.subtle.digest('SHA-256', data);
        const arr = Array.from(new Uint8Array(buf));
        th = arr.map(b => b.toString(16).padStart(2, '0')).join('');
      }

      this.metaData.ts = ts;
      this.metaData.th = th;
    }

    /**
     * Checks if the current environment supports the Web Crypto API's digest method.
     * The method verifies if the code is running in a secure context and ensures
     * the presence of the `window.crypto.subtle.digest` function.
     *
     * todo: could be noved to a uitls file, if needed somewhere else
     *
     * @return {boolean} Returns true if the Web Crypto API's digest method is available and the context is secure; otherwise, false.
     */
    private supportsWebCryptoDigest(): boolean {
      // do we have https?
      if (!window.isSecureContext) {
        return false;
      }

      // do we have window.crypto.subtle?
      const c = window.crypto as any;
      if (!c || !c.subtle || typeof c.subtle.digest !== 'function') {
        return false;
      }

      // all set
      return true;
    }
}

/**
 * Holds data for the Banktrnsfer
 */
export class PaymentBanktransfer {
    /**
     * Account owner
     */
    public receiverName: string;

    /**
     * IBAN
     */
    public receiverIban: string;

    /**
     * BIC
     */
    public receiverBic: string;

    /**
     * Reference
     */
    public paymentReference: string;

    /**
     * Reference
     */
    public paymentReferenceMessage: string;

}

/**
 * Model for holding payment information for debit payment
 */
export class PaymentDebit {
    /**
     * Name of the bank account holder
     */
    public name: string;

    /**
     * IBAN of the user
     */
    public iban: string;

    /**
     * BIC of the user
     */
    public bic: string;

    /**
     * If the user accepted the terms or not
     * @type {boolean}
     */
    public terms = false;
}

/**
 * Holds the meta date of a donation that must be send to the transaction API
 */
export class MetaData {
    /**
     * Token to Check if the donation is valid
     */
    public token: string;

    /**
     * Token hash for internal validation
     */
    public th: string;

    /**
     * timestamp, vcteating the donation
     */
    public ts: string;

    /**
     * Projectidentifier to know for which customer this donation is
     */
    public projectIdentifier: string;

    /**
     * Holds the project ID
     */
    public projectId: number;

    /**
     * ID of the organisation
     */
    public organisationId: number;

    /**
     * identifier of the campaign of this donation
     */
    public campaign: string;
}

/**
 * Holds some data for a event donation
 */
export class DonatorData {
    /**
     * Should the donation be visible in the donators list?
     */
    public showDonation = false;

    /**
     * Name of the donator
     */
    public name: string;

    /**
     * message from the donator to the supporter
     */
    public message: string;
}

/**
 * Holds some extra data for a gift donation
 */
export class GiftData {
    /**
     * Should the donation be a gift donation of not
     */
    public isGiftDonation = false;

    /**
     * Sender of the donation
     */
    public sender: string;

    /**
     * receiver of the donation
     */
    public receiver: string;
}

/**
 * If a (shop) donation should be send as gift, we need some more data for this.
 */
export class Gift {
    /**
     * toggle if the amount data should be hidden.
     */
    public hideAmount: boolean;

    /**
     * The product that should be visible as image
     */
    public product: string;

    /**
     * Name of the sender
     */
    public sender: string;

    /**
     * Message of the sender
     */
    public message: string;

    /**
     * list of receivers.
     */
    public receiver: {name: string}[] = [];
}
